import classNames from 'classnames';
import React from 'react';

import { gql } from '@reverbdotcom/commons/src/gql';
import { RCIcon, RCPopover } from '@reverbdotcom/cadence/components';
import { InfoIcon } from '@reverbdotcom/cadence/icons/react';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';

import { B_STOCK, conditionUuidForSlug, isCoreBrandNew } from '../condition_helpers';
import { I18N } from './translate';
import { TypicalNewPriceDisplayFieldsFragment } from '../gql/graphql';
import { useHelpCenterArticleUrl } from '../url_helpers';

const TNP_ARTICLE_ID = '37415010089107';

type Listing = TypicalNewPriceDisplayFieldsFragment;

interface Props {
  listing: Listing;
  onFeaturedListing?: boolean;
}

export default function TypicalNewPriceDisplay({ listing, onFeaturedListing }: Props) {
  const prefix = 'commons.priceWithShipping';
  const t = withPrefix(prefix);
  const tnpArticleUrl = useHelpCenterArticleUrl(TNP_ARTICLE_ID);

  if (!listing.pricing.typicalNewPriceDisplay || onFeaturedListing) return null;

  const isBrandNew = isCoreBrandNew(listing.condition.conditionUuid);
  const isBStock = conditionUuidForSlug(B_STOCK) === listing.condition.conditionUuid;
  const tnpAmountDisplay = listing.pricing.typicalNewPriceDisplay.amountDisplay;
  const descriptor = isBrandNew ? t('typicalNewPrice') : t('typicalNewPriceWithAmount', { price: tnpAmountDisplay });

  const translationKey = () => {
    if (isBrandNew) return prefix + '.typicalNewPricePopoverNew';
    else if (isBStock) return prefix + '.typicalNewPricePopoverBStock';
    else return prefix + '.typicalNewPricePopoverUsed';
  };
  const amountLabel = () => {
    if (isBrandNew) return tnpAmountDisplay;
    else if (isBStock) return t('saveWithBStock');
    else return t('saveWithUsed');
  };
  const popoverText = (
    <I18N
      html
      text={translationKey()}
      args={{
        linkOpen: `<a href='${tnpArticleUrl}' target=_blank class='text-link' rel='nofollow'>`,
        linkClose: '</a>',
      }}
    />
  );

  return (
    <div
      className={classNames(
        'typical-new-price-display',
        { 'typical-new-price-display--brand-new': isBrandNew },
        { 'typical-new-price-display--used': !isBrandNew },
      )}
    >
      <b className="mr-1">{amountLabel()}</b>
      {descriptor}
      <RCPopover
        anchor={
          <span className="size-fixed-100 ml-1" role="button">
            <RCIcon svgComponent={InfoIcon} size="inline" />
          </span>
        }
      >
        {popoverText}
      </RCPopover>
    </div>
  );
}

export const TypicalNewPriceDisplayFragment = gql(`
  fragment TypicalNewPriceDisplayFields on Listing {
    _id
    id
    condition {
      conditionUuid
    }
    pricing {
      typicalNewPriceDisplay {
        amountDisplay
      }
      originalPriceDescription
    }
  }
`);
